'use client';

/**
 * ContactForm Component
 * =====================
 *
 * A fully featured contact form with:
 * - Zod schema validation for all fields
 * - Web3Forms integration for email sending (free service)
 * - WhatsApp follow-up modal after successful submission
 *
 * HOW IT WORKS:
 * 1. User fills in the form (name, email, phone, message)
 * 2. On submit, Zod validates all fields
 * 3. If valid, form data is sent to Web3Forms API
 * 4. Web3Forms sends email to magnategroup1000@gmail.com
 * 5. Success modal appears with WhatsApp option for faster response
 *
 * WEB3FORMS:
 * - Free email service that doesn't require backend
 * - Works by sending form data to their API with an access key
 * - They forward it to your configured email
 * - Access key is public (safe to expose in frontend)
 */

import { useState, useCallback } from 'react';
import WhatsAppModal from './WhatsAppModal';
import type { ContactFormData } from './contactSchema';

// Validation schema lives in ./contactSchema and is loaded on demand (see validateForm)

// Type for validation errors - partial because not all fields may have errors
type ValidationErrors = Partial<Record<keyof ContactFormData, string>>;

// Form submission status type
type FormStatus = 'idle' | 'submitting' | 'success' | 'error';

// ============================================================
// WEB3FORMS CONFIGURATION
// ============================================================
// Web3Forms is a free form submission service.
// The access key identifies which email to send submissions to.
// This key is public and safe to include in frontend code.

const WEB3FORMS_ACCESS_KEY = 'ce66e14b-94ba-41ca-bf74-844e581c41d6';
const WEB3FORMS_ENDPOINT = 'https://api.web3forms.com/submit';

// WhatsApp business number
const WHATSAPP_NUMBER = '919664805202';

interface ContactFormProps {
  initialMessage?: string;
}

export default function ContactForm({ initialMessage = '' }: ContactFormProps) {
  // ============================================================
  // STATE MANAGEMENT
  // ============================================================

  // Form data state - holds all input values
  const [formData, setFormData] = useState<ContactFormData>({
    name: '',
    email: '',
    phone: '',
    message: initialMessage,
  });

  // Validation errors - displayed under each field
  const [errors, setErrors] = useState<ValidationErrors>({});

  // Form submission status
  const [status, setStatus] = useState<FormStatus>('idle');

  // WhatsApp modal visibility
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);

  // Store form data for WhatsApp message (before form reset)
  const [submittedData, setSubmittedData] = useState<ContactFormData | null>(null);

  // ============================================================
  // INPUT CHANGE HANDLER
  // ============================================================
  // This function updates form state when user types.
  // It also clears any existing error for that field.

  const handleChange = useCallback((
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;

    // Update form data
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));

    // Clear error for this field when user starts typing
    if (errors[name as keyof ContactFormData]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined,
      }));
    }
  }, [errors]);

  // ============================================================
  // FORM VALIDATION
  // ============================================================
  // Uses Zod's safeParse to validate without throwing errors.
  // Returns success boolean and formatted error messages.

  const validateForm = useCallback(async (): Promise<boolean> => {
    // Load Zod + the schema only when the user actually submits.
    // After the first submit the module is cached, so this is instant.
    const { contactFormSchema } = await import('./contactSchema');

    // safeParse returns { success: boolean, data?, error? }
    // It doesn't throw, making it perfect for form validation
    const result = contactFormSchema.safeParse(formData);

    if (!result.success) {
      // Format Zod errors into our ValidationErrors type
      // In Zod v4, errors are in result.error.issues (not .errors)
      // Each issue has: { path: string[], message: string, code: string, ... }
      const formattedErrors: ValidationErrors = {};

      result.error.issues.forEach((issue) => {
        const field = issue.path[0] as keyof ContactFormData;
        // Only keep the first error for each field
        if (field && !formattedErrors[field]) {
          formattedErrors[field] = issue.message;
        }
      });

      setErrors(formattedErrors);
      return false;
    }

    setErrors({});
    return true;
  }, [formData]);

  // ============================================================
  // FORM SUBMISSION HANDLER
  // ============================================================
  // Validates form, sends to Web3Forms API, handles response.

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate before submission
    if (!(await validateForm())) {
      return;
    }

    setStatus('submitting');

    // Store form data before resetting (for WhatsApp message)
    setSubmittedData(formData);

    try {
      // Prepare form data for Web3Forms
      // FormData is used because Web3Forms accepts multipart/form-data
      const formDataToSend = new FormData();
      formDataToSend.append('access_key', WEB3FORMS_ACCESS_KEY);
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email || '');
      formDataToSend.append('phone', formData.phone || '');
      formDataToSend.append('message', formData.message);
      formDataToSend.append('subject', 'New Inquiry from Magnate Regalia Website');
      formDataToSend.append('from_name', 'Magnate Regalia Website');

      // Send to Web3Forms API
      const response = await fetch(WEB3FORMS_ENDPOINT, {
        method: 'POST',
        body: formDataToSend,
      });

      const result = await response.json();

      if (result.success) {
        setStatus('success');

        // Reset form after successful submission
        setFormData({
          name: '',
          email: '',
          phone: '',
          message: '',
        });

        // Show WhatsApp modal after a short delay
        setTimeout(() => {
          setShowWhatsAppModal(true);
        }, 1000);

        // Reset status after 5 seconds
        setTimeout(() => {
          setStatus('idle');
        }, 5000);
      } else {
        throw new Error(result.message || 'Form submission failed');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setStatus('error');

      // Show WhatsApp modal even on error (as alternative contact method)
      setTimeout(() => {
        setShowWhatsAppModal(true);
      }, 1500);

      // Reset error status after 5 seconds
      setTimeout(() => {
        setStatus('idle');
      }, 5000);
    }
  };

  // ============================================================
  // WHATSAPP MESSAGE GENERATOR
  // ============================================================
  // Creates a pre-filled WhatsApp message with form data.

  const generateWhatsAppUrl = useCallback(() => {
    const data = submittedData || formData;

    const message = `Hi Magnate Group!

I just submitted an inquiry on your Magnate Regalia website.

*Contact Details:*
📝 Name: ${data.name}
📧 Email: ${data.email}
📞 Phone: ${data.phone}

*My Message:*
${data.message}

Looking forward to hearing from you!

Best regards,
${data.name}`;

    const encodedMessage = encodeURIComponent(message);
    return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodedMessage}`;
  }, [submittedData, formData]);

  // ============================================================
  // RENDER
  // ============================================================

  return (
    <>
      <form onSubmit={handleSubmit} className="space-y-4 flex-1 flex flex-col" noValidate>
        {/* Name Field */}
        <div>
          <label htmlFor="name" className="block text-white/90 text-sm mb-2">
            Full Name <span className="text-red-400">*</span>
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className={`w-full px-4 py-4 bg-white/10 border rounded-lg text-white placeholder-white/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 transition-colors backdrop-blur-sm ${
              errors.name
                ? 'border-red-400 focus:border-red-400'
                : 'border-white/20 focus:border-white/40'
            }`}
            placeholder="Enter your name"
            disabled={status === 'submitting'}
          />
          {errors.name && (
            <p className="mt-1 text-sm text-red-400">{errors.name}</p>
          )}
        </div>

        {/* Email Field */}
        <div>
          <label htmlFor="email" className="block text-white/90 text-sm mb-2">
            Email
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className={`w-full px-4 py-4 bg-white/10 border rounded-lg text-white placeholder-white/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 transition-colors backdrop-blur-sm ${
              errors.email
                ? 'border-red-400 focus:border-red-400'
                : 'border-white/20 focus:border-white/40'
            }`}
            placeholder="Enter your email"
            disabled={status === 'submitting'}
          />
          {errors.email && (
            <p className="mt-1 text-sm text-red-400">{errors.email}</p>
          )}
        </div>

        {/* Phone Field */}
        <div>
          <label htmlFor="phone" className="block text-white/90 text-sm mb-2">
            Phone
          </label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            className={`w-full px-4 py-4 bg-white/10 border rounded-lg text-white placeholder-white/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 transition-colors backdrop-blur-sm ${
              errors.phone
                ? 'border-red-400 focus:border-red-400'
                : 'border-white/20 focus:border-white/40'
            }`}
            placeholder="Enter your phone number"
            disabled={status === 'submitting'}
          />
          {errors.phone && (
            <p className="mt-1 text-sm text-red-400">{errors.phone}</p>
          )}
        </div>

        {/* Message Field */}
        <div className="flex-1">
          <label htmlFor="message" className="block text-white/90 text-sm mb-2">
            Message <span className="text-red-400">*</span>
          </label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            rows={4}
            maxLength={500}
            className={`w-full min-h-[120px] flex-1 px-4 py-4 bg-white/10 border rounded-lg text-white placeholder-white/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 transition-colors resize-none backdrop-blur-sm ${
              errors.message
                ? 'border-red-400 focus:border-red-400'
                : 'border-white/20 focus:border-white/40'
            }`}
            placeholder="Tell us what you're looking for..."
            disabled={status === 'submitting'}
          />
          <div className="flex justify-between mt-1">
            {errors.message ? (
              <p className="text-sm text-red-400">{errors.message}</p>
            ) : (
              <span />
            )}
            <span className="text-xs text-white/60">{formData.message.length}/500</span>
          </div>
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={status === 'submitting'}
          className={`w-full py-3 font-semibold rounded-lg transition-all duration-300 ${
            status === 'submitting'
              ? 'bg-amber-400/50 text-slate-900/50 cursor-not-allowed'
              : 'bg-amber-400 text-slate-900 hover:bg-amber-300 hover:scale-[1.02]'
          }`}
        >
          {status === 'submitting' ? (
            <span className="flex items-center justify-center gap-2">
              <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              Sending...
            </span>
          ) : (
            'Send Message'
          )}
        </button>

        {/* Success Message */}
        {status === 'success' && (
          <div className="p-4 bg-green-500/20 border border-green-500/40 rounded-lg text-green-300 text-center animate-fadeIn">
            Thank you! Your message has been sent successfully.
          </div>
        )}

        {/* Error Message */}
        {status === 'error' && (
          <div className="p-4 bg-red-500/20 border border-red-500/40 rounded-lg text-red-300 text-center animate-fadeIn">
            There was an error sending your message. Please try WhatsApp instead.
          </div>
        )}
      </form>

      {/* WhatsApp Modal */}
      <WhatsAppModal
        isOpen={showWhatsAppModal}
        onClose={() => setShowWhatsAppModal(false)}
        whatsappUrl={generateWhatsAppUrl()}
        isEmailSuccess={status === 'success'}
        phoneNumber={WHATSAPP_NUMBER}
      />
    </>
  );
}
